import Divider from '@lmc/ui-kit/src/layout/Divider/Divider'
import { Modal } from 'antd'
import { FC } from 'react'
import styled from 'styled-components'

type ModalLayoutProps = {
  headerContent?: React.ReactNode
  footerContent?: React.ReactNode
  children: React.ReactNode
  headerHeight?: number
  open: boolean
  onCancel: () => void
}

// Heights in vh
const TOTAL_HEIGHT = 90
const FOOTER_HEIGHT = 9
const HEADER_PADDING = 3
const BREAKPOINT_HEIGHT_DIFF = 2

const ModalLayout: FC<ModalLayoutProps> = ({
  headerContent,
  footerContent,
  children,
  headerHeight = 11, // Defaults to 11vh
  open,
  onCancel,
}) => {
  const headerHeightZoomed = headerHeight + BREAKPOINT_HEIGHT_DIFF
  const scrollableHeight =
    TOTAL_HEIGHT - headerHeight - FOOTER_HEIGHT - HEADER_PADDING
  const scrollableHeightZoomed =
    TOTAL_HEIGHT - headerHeightZoomed - FOOTER_HEIGHT - HEADER_PADDING
  const topGradientPosition = headerHeight + HEADER_PADDING
  const topGradientPositionZoomed =
    headerHeight + HEADER_PADDING + BREAKPOINT_HEIGHT_DIFF

  return (
    <StyledModal
      centered
      width={1160}
      open={open}
      onCancel={onCancel}
      destroyOnClose
      footer={null}
    >
      <TopGradientOverlay
        topPosition={topGradientPosition}
        topPositionZoomed={topGradientPositionZoomed}
      />
      <Header height={headerHeight} heightZoomed={headerHeightZoomed}>
        {headerContent}
      </Header>
      <ScrollableContent
        height={scrollableHeight}
        heightZoomed={scrollableHeightZoomed}
        data-cy="container-modal-content"
      >
        <FormWrapper data-cy="container-modal-form">{children}</FormWrapper>
      </ScrollableContent>
      <GradientOverlay />
      <Divider />
      <FooterWrapper data-cy="container-modal-footer">{footerContent}</FooterWrapper>
    </StyledModal>
  )
}

export default ModalLayout

const StyledModal = styled(Modal)`
  .ant-modal-content {
    height: ${TOTAL_HEIGHT}vh;
    overflow: hidden;
    padding: ${HEADER_PADDING}vh 3vw;
  }
  height: ${TOTAL_HEIGHT}vh;
`

const Header = styled.div<{ height: number; heightZoomed: number }>`
  height: ${({ height }) => `${height}vh`};
  ${({ theme }) => theme.mediaQueries?.tabletDown} {
    height: ${({ heightZoomed }) => `${heightZoomed}vh`};
  }
`

const ScrollableContent = styled.div<{ height: number; heightZoomed: number }>`
  height: ${({ height }) => `${height}vh`};
  overflow-y: auto;

  ${({ theme }) => theme.mediaQueries?.tabletDown} {
    height: ${({ heightZoomed }) => `${heightZoomed}vh`};
  }
`

const FooterWrapper = styled.footer`
  height: ${FOOTER_HEIGHT}vh;
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  padding: 3vh 3vw;
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 5;
`

const FormWrapper = styled.div`
  padding: 2vh 2vw;
`

const GradientOverlay = styled.div`
  position: absolute;
  bottom: ${FOOTER_HEIGHT}vh;
  left: 0;
  right: 0;
  height: 3vh;
  background: linear-gradient(
    180deg,
    rgba(255, 255, 255, 0) 0%,
    rgba(255, 255, 255, 1) 100%
  );
  pointer-events: none;
  z-index: 5;
`

const TopGradientOverlay = styled.div<{
  topPosition: number
  topPositionZoomed: number
}>`
  position: absolute;
  top: ${({ topPosition }) => `${topPosition}vh`};
  left: 0;
  right: 0;
  height: 3vh;
  background: linear-gradient(
    0deg,
    rgba(255, 255, 255, 0) 0%,
    rgba(255, 255, 255, 1) 100%
  );
  pointer-events: none;
  z-index: 5;

  ${({ theme }) => theme.mediaQueries?.tabletDown} {
    top: ${({ topPositionZoomed }) => `${topPositionZoomed}vh`};
  }
`
